/**
 * TileOccupancy - manages tile occupation for building placement
 * Ensures buildings cannot overlap and tracks which tiles are occupied
 */

import { tileHeight, isLand } from '@/rendering/terrain/TerrainGenerator';
import { BUILDING_STATS } from '@/game/factions/FactionData';
import type { BuildingType, BuildingSize, Entity, FactionId } from '@/types';
import { clearPathfindingCache } from '@/core/utils/Pathfinding';
import { obstacleRegistry } from '@/game/systems/ObstacleRegistry';

export interface PlacementResult {
  valid: boolean;
  reason?: 'tile_occupied' | 'not_land' | 'invalid_terrain' | 'not_in_territory' | 'obstacle' | 'unit_present';
  occupiedTiles?: { x: number; z: number }[];
}

// Callback type for territory ownership check
export type TerritoryCheckCallback = (worldX: number, worldZ: number) => FactionId | null;

// Callback type for unit presence check
export type UnitCheckCallback = (tileX: number, tileZ: number) => boolean;

export interface OccupiedTileInfo {
  entityId: Entity;
  buildingType: BuildingType;
}

export class TileOccupancy {
  // Map: "tileX,tileZ" -> OccupiedTileInfo
  private occupiedTiles: Map<string, OccupiedTileInfo> = new Map();
  // Map: entityId -> list of tile keys
  private entityTiles: Map<Entity, string[]> = new Map();
  // Callback for territory ownership check
  private territoryCheck: TerritoryCheckCallback | null = null;
  // Callback for unit presence check
  private unitCheck: UnitCheckCallback | null = null;
  // Current player faction for territory validation
  private playerFaction: FactionId | null = null;

  /**
   * Set the territory check callback (called from Game.ts)
   */
  setTerritoryCheck(callback: TerritoryCheckCallback): void {
    this.territoryCheck = callback;
  }

  /**
   * Set the unit check callback (called from Game.ts)
   */
  setUnitCheck(callback: UnitCheckCallback): void {
    this.unitCheck = callback;
  }

  /**
   * Set the player faction for territory validation
   */
  setPlayerFaction(factionId: FactionId): void {
    this.playerFaction = factionId;
  }

  /**
   * Get tile key from coordinates
   */
  private getTileKey(x: number, z: number): string {
    return `${Math.floor(x)},${Math.floor(z)}`;
  }

  /**
   * Get all tiles that would be occupied by a building at the given center position
   */
  getTilesForBuilding(centerX: number, centerZ: number, size: BuildingSize): { x: number; z: number }[] {
    const tiles: { x: number; z: number }[] = [];

    // Calculate tile coordinates (center the building on the grid)
    const baseTileX = Math.floor(centerX);
    const baseTileZ = Math.floor(centerZ);

    // Calculate offsets to center the building
    const offsetX = Math.floor(size.width / 2);
    const offsetZ = Math.floor(size.depth / 2);

    for (let dz = 0; dz < size.depth; dz++) {
      for (let dx = 0; dx < size.width; dx++) {
        tiles.push({
          x: baseTileX - offsetX + dx,
          z: baseTileZ - offsetZ + dz,
        });
      }
    }

    return tiles;
  }

  /**
   * Check if a specific tile is occupied
   */
  isOccupied(tileX: number, tileZ: number): boolean {
    return this.occupiedTiles.has(this.getTileKey(tileX, tileZ));
  }

  /**
   * Get the entity occupying a tile
   */
  getOccupyingEntity(tileX: number, tileZ: number): OccupiedTileInfo | null {
    return this.occupiedTiles.get(this.getTileKey(tileX, tileZ)) || null;
  }

  /**
   * Check if a building can be placed at the given position
   */
  canPlaceBuilding(centerX: number, centerZ: number, buildingType: BuildingType): PlacementResult {
    const stats = BUILDING_STATS[buildingType];
    if (!stats) {
      return { valid: false, reason: 'invalid_terrain' };
    }

    const tiles = this.getTilesForBuilding(centerX, centerZ, stats.size);

    for (const tile of tiles) {
      // Check 1: Is tile already occupied by a building?
      if (this.isOccupied(tile.x, tile.z)) {
        return {
          valid: false,
          reason: 'tile_occupied',
          occupiedTiles: tiles,
        };
      }

      // Check 2: Is tile on land?
      const worldX = tile.x + 0.5;
      const worldZ = tile.z + 0.5;
      const h = tileHeight(worldX, worldZ);

      if (!isLand(h)) {
        return {
          valid: false,
          reason: 'not_land',
          occupiedTiles: tiles,
        };
      }

      // Check 3: Is there an obstacle (tree, rock, etc.)?
      if (obstacleRegistry.hasObstacle(tile.x, tile.z)) {
        return {
          valid: false,
          reason: 'obstacle',
          occupiedTiles: tiles,
        };
      }

      // Check 4: Is there a unit on this tile?
      if (this.unitCheck && this.unitCheck(tile.x, tile.z)) {
        return {
          valid: false,
          reason: 'unit_present',
          occupiedTiles: tiles,
        };
      }

      // Check 5: Is tile within player's territory?
      if (this.territoryCheck && this.playerFaction) {
        const owner = this.territoryCheck(worldX, worldZ);
        if (owner !== this.playerFaction) {
          return {
            valid: false,
            reason: 'not_in_territory',
            occupiedTiles: tiles,
          };
        }
      }
    }

    return { valid: true, occupiedTiles: tiles };
  }

  /**
   * Occupy tiles for a building
   */
  occupyTiles(centerX: number, centerZ: number, buildingType: BuildingType, entityId: Entity): void {
    const stats = BUILDING_STATS[buildingType];
    if (!stats) return;

    const tiles = this.getTilesForBuilding(centerX, centerZ, stats.size);
    const tileKeys: string[] = [];

    for (const tile of tiles) {
      const key = this.getTileKey(tile.x, tile.z);
      this.occupiedTiles.set(key, { entityId, buildingType });
      tileKeys.push(key);
    }

    this.entityTiles.set(entityId, tileKeys);

    // Clear pathfinding cache since obstacles changed
    clearPathfindingCache();
  }

  /**
   * Release tiles when a building is destroyed
   */
  releaseTiles(entityId: Entity): void {
    const tileKeys = this.entityTiles.get(entityId);
    if (!tileKeys) return;

    for (const key of tileKeys) {
      this.occupiedTiles.delete(key);
    }

    this.entityTiles.delete(entityId);

    // Clear pathfinding cache since obstacles changed
    clearPathfindingCache();
  }

  /**
   * Get building size for a type
   */
  getBuildingSize(buildingType: BuildingType): BuildingSize {
    const stats = BUILDING_STATS[buildingType];
    return stats?.size || { width: 2, depth: 2 };
  }

  /**
   * Clear all occupancy data
   */
  clear(): void {
    this.occupiedTiles.clear();
    this.entityTiles.clear();
  }

  /**
   * Get total number of occupied tiles
   */
  getOccupiedTileCount(): number {
    return this.occupiedTiles.size;
  }
}

// Singleton instance for global access
export const tileOccupancy = new TileOccupancy();
